import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  DollarSign, 
  LogOut,
  Users,
  AlertTriangle,
  Settings,
  ArrowLeft,
  Loader2,
  LayoutDashboard,
  Briefcase,
  Bot,
  FileText,
  CreditCard,
  MessageSquare,
  Clock,
  CheckCircle,
  XCircle,
  X,
  Play,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  UserCheck,
  RefreshCw,
  AlertCircle,
  Mail,
  Phone,
  MapPin,
  Volume2,
  Send,
  ArrowDownLeft,
  ArrowUpRight,
  Target,
  TrendingUp,
  Sparkles,
  Zap
} from 'lucide-react';
import { serenityClient } from '../services/serenityClient';
import { workflowService } from '../services/workflowService';
import { AGENT_CODES } from '../config/environment';
import CommunicationsTimeline from '../components/CommunicationsTimeline';
import OperatorAssistant from '../components/OperatorAssistant';
import type { Agent, Case, Debtor, PaymentPlan, Payment, Document, Communication, ExceptionRequest, WorkflowTask } from '../types';

interface CaseDetailPageProps {
  user: Agent;
  onLogout: () => void;
}

// Component to render AI results in a user-friendly format
function AIResultDisplay({ content, type }: { content: string; type: 'segmentation' | 'communication' | 'legal_assessment' | 'general' }) {
  const parsed = useMemo(() => {
    try {
      return JSON.parse(content);
    } catch {
      return null;
    }
  }, [content]);

  if (!parsed) {
    return <p className="whitespace-pre-wrap text-gray-700">{content}</p>;
  }

  // Segmentation result
  if (type === 'segmentation' || parsed.segmentation) {
    const seg = parsed.segmentation || parsed;
    const strategy = parsed.recommended_strategy;
    const nextAction = parsed.next_best_action;
    const flags = parsed.flags || [];

    return (
      <div className="space-y-4">
        {/* Segment Badge */}
        <div className="flex items-center gap-4">
          <div className={`px-4 py-2 rounded-full text-lg font-bold ${
            seg.segment === 'high' ? 'bg-green-100 text-green-800' :
            seg.segment === 'medium' ? 'bg-yellow-100 text-yellow-800' :
            'bg-red-100 text-red-800'
          }`}>
            <Target className="w-5 h-5 inline mr-2" />
            Segmento: {seg.segment?.toUpperCase()}
          </div>
          {seg.risk_score !== undefined && (
            <div className="text-gray-600">
              <span className="font-medium">Risk Score:</span> {seg.risk_score}/100
            </div>
          )}
          {seg.payment_probability !== undefined && (
            <div className="text-gray-600">
              <span className="font-medium">Prob. Pago:</span> {(seg.payment_probability * 100).toFixed(0)}%
            </div>
          )}
        </div>

        {/* Strategy */}
        {strategy && (
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-blue-800 flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4" /> Estrategia Recomendada
            </h4>
            <div className="space-y-2 text-sm text-blue-900">
              {strategy.collection_approach && <p><strong>Enfoque:</strong> {strategy.collection_approach}</p>}
              {strategy.contact_frequency && <p><strong>Frecuencia contacto:</strong> {strategy.contact_frequency}</p>}
              {strategy.payment_terms && <p><strong>Términos de pago:</strong> {strategy.payment_terms}</p>}
            </div>
          </div>
        )}

        {/* Next Action */}
        {nextAction && (
          <div className="bg-purple-50 p-4 rounded-lg">
            <h4 className="font-semibold text-purple-800 flex items-center gap-2 mb-2">
              <Zap className="w-4 h-4" /> Próxima Acción
            </h4>
            <div className="space-y-1 text-sm text-purple-900">
              <p><strong>Acción:</strong> {nextAction.action}</p>
              <p><strong>Canal:</strong> {nextAction.channel}</p>
              <p><strong>Timing:</strong> {nextAction.timing}</p>
            </div>
          </div>
        )}

        {/* Flags */}
        {flags.length > 0 && (
          <div className="bg-amber-50 p-3 rounded-lg">
            <h4 className="font-semibold text-amber-800 text-sm mb-2">Alertas</h4>
            <div className="flex flex-wrap gap-1">
              {flags.slice(0, 5).map((flag: string, i: number) => (
                <span key={i} className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded">
                  {flag.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Communication result
  if (type === 'communication' || parsed.communication || parsed.message_draft) {
    const comm = parsed.communication || parsed;
    return (
      <div className="space-y-4">
        {comm.channel && (
          <div className="flex items-center gap-2">
            <span className="font-medium">Canal:</span>
            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">{comm.channel}</span>
          </div>
        )}
        {(comm.subject || parsed.subject) && (
          <div>
            <span className="font-medium text-gray-600">Asunto:</span>
            <p className="font-semibold">{comm.subject || parsed.subject}</p>
          </div>
        )}
        {(comm.message_draft || comm.body || parsed.body || parsed.content) && (
          <div className="bg-gray-50 p-4 rounded-lg border">
            <p className="font-medium text-gray-600 mb-2">Borrador del mensaje:</p>
            <div className="whitespace-pre-wrap text-gray-800">
              {comm.message_draft || comm.body || parsed.body || parsed.content}
            </div>
          </div>
        )}
        {comm.tone && <p><strong>Tono:</strong> {comm.tone}</p>}
        {comm.call_to_action && <p><strong>CTA:</strong> {comm.call_to_action}</p>}
      </div>
    );
  }

  // Legal assessment result
  if (type === 'legal_assessment' || parsed.legal_assessment || parsed.assessment) {
    const legal = parsed.legal_assessment || parsed.assessment || parsed;
    return (
      <div className="space-y-4">
        {legal.recommendation && (
          <div className={`p-4 rounded-lg ${
            legal.recommendation.includes('proceed') || legal.recommendation.includes('aprobar') 
              ? 'bg-green-50 text-green-800' 
              : legal.recommendation.includes('not') || legal.recommendation.includes('no')
              ? 'bg-red-50 text-red-800'
              : 'bg-yellow-50 text-yellow-800'
          }`}>
            <h4 className="font-semibold flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> Recomendación
            </h4>
            <p className="mt-1">{legal.recommendation}</p>
          </div>
        )}
        {legal.viability_score !== undefined && (
          <div className="flex items-center gap-2">
            <span className="font-medium">Viabilidad:</span>
            <div className="flex-1 bg-gray-200 rounded-full h-3 max-w-xs">
              <div 
                className={`h-3 rounded-full ${
                  legal.viability_score >= 70 ? 'bg-green-500' :
                  legal.viability_score >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                }`}
                style={{ width: `${legal.viability_score}%` }}
              />
            </div>
            <span className="font-bold">{legal.viability_score}%</span>
          </div>
        )}
        {legal.estimated_recovery && (
          <p><strong>Recuperación estimada:</strong> €{legal.estimated_recovery.toLocaleString()}</p>
        )}
        {legal.timeline && (
          <p><strong>Timeline estimado:</strong> {legal.timeline}</p>
        )}
        {legal.risks && Array.isArray(legal.risks) && legal.risks.length > 0 && (
          <div className="bg-red-50 p-3 rounded-lg">
            <h4 className="font-semibold text-red-800 text-sm mb-2">Riesgos</h4>
            <ul className="list-disc list-inside text-sm text-red-700">
              {legal.risks.map((risk: string, i: number) => <li key={i}>{risk}</li>)}
            </ul>
          </div>
        )}
      </div>
    );
  }

  // Generic fallback - show formatted JSON sections
  return (
    <div className="space-y-3">
      {Object.entries(parsed).map(([key, value]) => (
        <div key={key} className="bg-gray-50 p-3 rounded-lg">
          <h4 className="font-semibold text-gray-700 capitalize mb-1">{key.replace(/_/g, ' ')}</h4>
          <div className="text-sm text-gray-600">
            {typeof value === 'string' ? (
              <p className="whitespace-pre-wrap">{value}</p>
            ) : (
              <pre className="text-xs overflow-auto">{JSON.stringify(value, null, 2)}</pre>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function CaseDetailPage({ user, onLogout }: CaseDetailPageProps) {
  const { caseId } = useParams<{ caseId: string }>();
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [debtor, setDebtor] = useState<Debtor | null>(null);
  const [paymentPlan, setPaymentPlan] = useState<PaymentPlan | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [communications, setCommunications] = useState<Communication[]>([]);
  const [requests, setRequests] = useState<ExceptionRequest[]>([]);
  const [tasks, setTasks] = useState<WorkflowTask[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'payments' | 'documents' | 'communications' | 'tasks'>('overview');
  const [expandedTask, setExpandedTask] = useState<string | null>(null);
  const [runningTaskId, setRunningTaskId] = useState<string | null>(null);

  useEffect(() => {
    if (caseId) {
      loadCaseData();
    }
  }, [caseId]);

  const loadCaseData = async () => {
    setIsLoading(true);
    try {
      // Load case
      const caseResult = await serenityClient.queryData<Case>(
        `SELECT * FROM Cases WHERE case_id = '${caseId}' LIMIT 1`
      );
      if (caseResult[0]) {
        setCaseData(caseResult[0]);
        
        // Load debtor
        const debtorResult = await serenityClient.queryData<Debtor>(
          `SELECT * FROM Debtors WHERE debtor_id = '${caseResult[0].debtor_id}' LIMIT 1`
        );
        setDebtor(debtorResult[0] || null);
      }

      // Load related data - use Promise.allSettled to handle individual failures gracefully
      const [planResult, paymentsResult, docsResult, commsResult, reqsResult, tasksResult] = await Promise.allSettled([
        serenityClient.queryData<PaymentPlan>(`SELECT * FROM PaymentPlans WHERE case_id = '${caseId}' ORDER BY created_at DESC LIMIT 1`),
        serenityClient.queryData<Payment>(`SELECT * FROM Payments WHERE case_id = '${caseId}' ORDER BY payment_date DESC`),
        serenityClient.queryData<Document>(`SELECT * FROM Documents WHERE case_id = '${caseId}' ORDER BY uploaded_at DESC`),
        serenityClient.queryData<Communication>(`SELECT * FROM Communications WHERE case_id = '${caseId}' ORDER BY sent_at DESC LIMIT 10`),
        serenityClient.queryData<ExceptionRequest>(`SELECT * FROM ExceptionRequests WHERE case_id = '${caseId}' ORDER BY created_at DESC`),
        workflowService.getTasksForCase(caseId).catch(() => [] as WorkflowTask[]),
      ]);

      setPaymentPlan(planResult.status === 'fulfilled' ? planResult.value[0] || null : null);
      setPayments(paymentsResult.status === 'fulfilled' ? paymentsResult.value : []);
      setDocuments(docsResult.status === 'fulfilled' ? docsResult.value : []);
      setCommunications(commsResult.status === 'fulfilled' ? commsResult.value : []);
      setRequests(reqsResult.status === 'fulfilled' ? reqsResult.value : []);
      setTasks(tasksResult.status === 'fulfilled' ? tasksResult.value : []);
    } catch (error) {
      console.error('Failed to load case data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Task actions
  const handleStartTask = async (taskId: string) => {
    try {
      await workflowService.startTask(taskId);
      setTasks(prev => prev.map(t => 
        t.task_id === taskId ? { ...t, status: 'in_progress', started_at: new Date().toISOString() } : t
      ));
    } catch (error) {
      console.error('Failed to start task:', error);
    }
  };

  const handleCompleteTask = async (taskId: string) => {
    try {
      await workflowService.completeTask(taskId, 'Completed by agent');
      setTasks(prev => prev.map(t => 
        t.task_id === taskId ? { ...t, status: 'completed', completed_at: new Date().toISOString() } : t
      ));
    } catch (error) {
      console.error('Failed to complete task:', error);
    }
  };

  const handleRerunAI = async (taskId: string) => {
    setRunningTaskId(taskId);
    try {
      const result = await workflowService.rerunAIAnalysis(taskId);
      setTasks(prev => prev.map(t => 
        t.task_id === taskId ? { 
          ...t, 
          ai_recommendation: result.recommendation,
          ai_confidence: result.confidence 
        } : t
      ));
    } catch (error) {
      console.error('Failed to re-run AI:', error);
    } finally {
      setRunningTaskId(null);
    }
  };

  // State for AI results modal
  const [aiResultModal, setAiResultModal] = useState<{isOpen: boolean; title: string; content: string} | null>(null);

  const handleCreateManualTask = async (taskType: 'segmentation' | 'legal_assessment' | 'communication') => {
    if (!caseData || !debtor) return;
    
    const taskConfig = {
      segmentation: { title: 'Segmentación del Deudor', agentCode: AGENT_CODES.SEGMENTADOR },
      legal_assessment: { title: 'Evaluación Legal', agentCode: AGENT_CODES.JUDICIAL },
      communication: { title: 'Generar Comunicación', agentCode: AGENT_CODES.COMUNICADOR },
    };
    
    const config = taskConfig[taskType];
    setRunningTaskId(taskType);
    
    try {
      // Execute agent directly instead of creating workflow task
      const params = [
        { Key: 'caseId', Value: caseData.case_id },
        { Key: 'debtorId', Value: debtor.debtor_id },
        { Key: 'debtorName', Value: `${debtor.first_name} ${debtor.last_name || ''}`.trim() },
        { Key: 'totalAmount', Value: String(caseData.total_amount) },
        { Key: 'status', Value: caseData.status },
        { Key: 'daysOverdue', Value: String(caseData.days_overdue) },
      ];
      
      const result = await serenityClient.executeAgent(config.agentCode, params);
      
      // Show result in modal
      setAiResultModal({
        isOpen: true,
        title: config.title,
        content: result.content || 'Análisis completado. No se generaron recomendaciones adicionales.',
      });
    } catch (error) {
      console.error('Failed to execute AI agent:', error);
      setAiResultModal({
        isOpen: true,
        title: 'Error',
        content: 'No se pudo ejecutar el agente de IA. Por favor, intenta de nuevo.',
      });
    } finally {
      setRunningTaskId(null);
    }
  };

  const getTaskStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'in_progress': return <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />;
      case 'awaiting_input': return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      case 'escalated': return <AlertTriangle className="w-5 h-5 text-red-600" />;
      default: return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getTaskPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'normal': return 'bg-blue-100 text-blue-800';
      case 'low': return 'bg-gray-100 text-gray-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const formatCurrency = (amount: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': case 'in_contact': return 'bg-blue-100 text-blue-800';
      case 'negotiating': case 'arrangement': return 'bg-yellow-100 text-yellow-800';
      case 'paying': return 'bg-green-100 text-green-800';
      case 'broken_arrangement': case 'pre_legal': case 'legal': return 'bg-red-100 text-red-800';
      case 'paid_in_full': case 'settled': return 'bg-emerald-100 text-emerald-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/cases', icon: Briefcase, label: 'Cases', active: true },
    { path: '/debtors', icon: Users, label: 'Debtors' },
    { path: '/requests', icon: AlertTriangle, label: 'Requests' },
    { path: '/settings', icon: Settings, label: 'Settings' },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Case Not Found</h2>
          <Link to="/cases" className="text-blue-600 hover:underline">Back to Cases</Link>
        </div>
      </div>
    );
  }

  const remainingBalance = caseData.total_amount - (caseData.amount_paid || 0);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-white flex flex-col">
        <div className="p-4 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6" />
            </div>
            <div>
              <h1 className="font-semibold">DFS Collections</h1>
              <p className="text-xs text-slate-400">Agent Portal</p>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {navItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    item.active ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center text-sm font-medium">
                {user.first_name[0]}{user.last_name[0]}
              </div>
              <div>
                <p className="text-sm font-medium">{user.first_name}</p>
                <p className="text-xs text-slate-400">{user.role}</p>
              </div>
            </div>
            <button onClick={onLogout} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Link to="/cases" className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900">{caseData.case_id}</h1>
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(caseData.status)}`}>
                  {getStatusLabel(caseData.status)}
                </span>
              </div>
              <p className="text-gray-500">{caseData.origin} · {caseData.cedent}</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(remainingBalance)}</p>
              <p className="text-sm text-gray-500">Remaining balance</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="flex gap-8">
              {(['overview', 'payments', 'documents', 'communications', 'tasks'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                    activeTab === tab
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab === 'tasks' && <ClipboardList className="w-4 h-4" />}
                  {tab === 'tasks' ? 'Tasks' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                  {tab === 'tasks' && tasks.filter(t => t.status !== 'completed').length > 0 && (
                    <span className="ml-1 px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                      {tasks.filter(t => t.status !== 'completed').length}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Case Info */}
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Case Details</h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div><p className="text-sm text-gray-500">Principal</p><p className="font-semibold">{formatCurrency(caseData.principal_amount || 0)}</p></div>
                    <div><p className="text-sm text-gray-500">Total Amount</p><p className="font-semibold">{formatCurrency(caseData.total_amount)}</p></div>
                    <div><p className="text-sm text-gray-500">Amount Paid</p><p className="font-semibold text-green-600">{formatCurrency(caseData.amount_paid || 0)}</p></div>
                    <div><p className="text-sm text-gray-500">Days Overdue</p><p className="font-semibold text-red-600">{caseData.days_overdue} days</p></div>
                    <div><p className="text-sm text-gray-500">Origin</p><p className="font-semibold">{caseData.origin}</p></div>
                    <div><p className="text-sm text-gray-500">Priority</p><p className="font-semibold">{caseData.priority}</p></div>
                    <div><p className="text-sm text-gray-500">Created</p><p className="font-semibold">{formatDate(caseData.created_at)}</p></div>
                    <div><p className="text-sm text-gray-500">Assigned Agent</p><p className="font-semibold">{caseData.assigned_agent || 'Unassigned'}</p></div>
                  </div>
                </div>

                {/* Payment Plan */}
                {paymentPlan && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Active Payment Plan</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div><p className="text-sm text-gray-500">Plan Type</p><p className="font-semibold">{getStatusLabel(paymentPlan.plan_type)}</p></div>
                      <div><p className="text-sm text-gray-500">Installment</p><p className="font-semibold">{formatCurrency(paymentPlan.installment_amount || 0)}</p></div>
                      <div><p className="text-sm text-gray-500">Frequency</p><p className="font-semibold">{paymentPlan.installment_frequency}</p></div>
                      <div><p className="text-sm text-gray-500">Next Payment</p><p className="font-semibold">{paymentPlan.next_payment_date ? formatDate(paymentPlan.next_payment_date) : 'N/A'}</p></div>
                    </div>
                  </div>
                )}

                {/* Exception Requests */}
                {requests.length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Exception Requests</h2>
                    <div className="space-y-3">
                      {requests.map((req) => (
                        <div key={req.request_id} className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium">{req.request_type}</span>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              req.status === 'approved' ? 'bg-green-100 text-green-800' :
                              req.status === 'denied' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>{getStatusLabel(req.status)}</span>
                          </div>
                          <p className="text-sm text-gray-600">{req.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Debtor Info */}
              <div className="space-y-6">
                {debtor && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Debtor Information</h2>
                    <div className="space-y-3">
                      <div><p className="text-sm text-gray-500">Name</p><p className="font-semibold">{debtor.debtor_type === 'corporate' ? debtor.first_name : `${debtor.first_name} ${debtor.last_name}`}</p></div>
                      <div><p className="text-sm text-gray-500">Type</p><p className="font-semibold">{debtor.debtor_type}</p></div>
                      <div><p className="text-sm text-gray-500">Email</p><p className="font-semibold">{debtor.email}</p></div>
                      <div><p className="text-sm text-gray-500">Phone</p><p className="font-semibold">{debtor.phone || 'N/A'}</p></div>
                      <div><p className="text-sm text-gray-500">Segment</p>
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          debtor.segment === 'high' ? 'bg-green-100 text-green-800' :
                          debtor.segment === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>{debtor.segment}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'payments' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment History</h2>
              {payments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No payments recorded.</div>
              ) : (
                <div className="space-y-3">
                  {payments.map((payment) => (
                    <div key={payment.payment_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        {payment.status === 'completed' ? <CheckCircle className="w-5 h-5 text-green-600" /> :
                         payment.status === 'failed' ? <XCircle className="w-5 h-5 text-red-600" /> :
                         <Clock className="w-5 h-5 text-yellow-600" />}
                        <div>
                          <p className="font-medium">{formatCurrency(payment.amount)}</p>
                          <p className="text-sm text-gray-500">{formatDate(payment.payment_date)} · {payment.payment_method}</p>
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        payment.status === 'completed' ? 'bg-green-100 text-green-800' :
                        payment.status === 'failed' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>{payment.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Documents</h2>
              {documents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No documents uploaded.</div>
              ) : (
                <div className="space-y-3">
                  {documents.map((doc) => (
                    <div key={doc.document_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-blue-600" />
                        <div>
                          <p className="font-medium">{doc.document_name}</p>
                          <p className="text-sm text-gray-500">{doc.document_type} · {formatDate(doc.uploaded_at)}</p>
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        doc.status === 'approved' ? 'bg-green-100 text-green-800' :
                        doc.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>{getStatusLabel(doc.status)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'communications' && (
            <CommunicationsTimeline 
              communications={communications} 
              formatDate={formatDate}
            />
          )}

          {activeTab === 'tasks' && (
            <div className="space-y-4">
              {/* Header with quick actions */}
              <div className="bg-gradient-to-r from-slate-700 to-slate-800 rounded-xl p-6 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <ClipboardList className="w-8 h-8" />
                    <div>
                      <h2 className="text-xl font-semibold">Case Tasks</h2>
                      <p className="text-slate-300 text-sm">
                        {tasks.filter(t => t.status !== 'completed').length} pending · {tasks.filter(t => t.status === 'completed').length} completed
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleCreateManualTask('segmentation')}
                      disabled={runningTaskId === 'segmentation'}
                      className="px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm flex items-center gap-2 transition-colors"
                    >
                      {runningTaskId === 'segmentation' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" />}
                      Segment
                    </button>
                    <button
                      onClick={() => handleCreateManualTask('communication')}
                      disabled={runningTaskId === 'communication'}
                      className="px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm flex items-center gap-2 transition-colors"
                    >
                      {runningTaskId === 'communication' ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />}
                      Generate Comm
                    </button>
                    <button
                      onClick={() => handleCreateManualTask('legal_assessment')}
                      disabled={runningTaskId === 'legal_assessment'}
                      className="px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm flex items-center gap-2 transition-colors"
                    >
                      {runningTaskId === 'legal_assessment' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Briefcase className="w-4 h-4" />}
                      Legal Review
                    </button>
                  </div>
                </div>
              </div>

              {/* Tasks List */}
              {tasks.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
                  <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No tasks for this case yet.</p>
                  <p className="text-sm text-gray-400 mt-1">Tasks are created automatically when documents are uploaded or requests are submitted.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {tasks.map((task) => (
                    <div key={task.task_id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      <div 
                        className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                        onClick={() => setExpandedTask(expandedTask === task.task_id ? null : task.task_id)}
                      >
                        <div className="flex items-center gap-4">
                          {getTaskStatusIcon(task.status)}
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-gray-900">{task.title}</p>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${getTaskPriorityColor(task.priority)}`}>
                                {task.priority}
                              </span>
                              {task.ai_agent_code && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 flex items-center gap-1">
                                  <Bot className="w-3 h-3" /> AI
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500">{task.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {task.status === 'pending' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleStartTask(task.task_id); }}
                              className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1"
                            >
                              <Play className="w-3 h-3" /> Start
                            </button>
                          )}
                          {task.status === 'in_progress' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleCompleteTask(task.task_id); }}
                              className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 flex items-center gap-1"
                            >
                              <CheckCircle className="w-3 h-3" /> Complete
                            </button>
                          )}
                          {task.status === 'awaiting_input' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleCompleteTask(task.task_id); }}
                              className="px-3 py-1.5 bg-yellow-600 text-white text-sm rounded-lg hover:bg-yellow-700 flex items-center gap-1"
                            >
                              <UserCheck className="w-3 h-3" /> Approve
                            </button>
                          )}
                          {task.ai_agent_code && task.status !== 'completed' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleRerunAI(task.task_id); }}
                              disabled={runningTaskId === task.task_id}
                              className="px-3 py-1.5 bg-purple-100 text-purple-700 text-sm rounded-lg hover:bg-purple-200 flex items-center gap-1"
                            >
                              {runningTaskId === task.task_id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <RefreshCw className="w-3 h-3" />
                              )}
                              Re-run AI
                            </button>
                          )}
                          {expandedTask === task.task_id ? 
                            <ChevronUp className="w-5 h-5 text-gray-400" /> : 
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          }
                        </div>
                      </div>
                      
                      {expandedTask === task.task_id && (
                        <div className="border-t border-gray-200 p-4 bg-gray-50">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                            <div>
                              <p className="text-gray-500">Created</p>
                              <p className="font-medium">{formatDate(task.created_at)}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Due Date</p>
                              <p className="font-medium">{task.due_date ? formatDate(task.due_date) : 'Not set'}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Assigned To</p>
                              <p className="font-medium">{task.assigned_to || task.assigned_role || 'Unassigned'}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Type</p>
                              <p className="font-medium">{task.task_type.replace(/_/g, ' ')}</p>
                            </div>
                          </div>
                          
                          {task.ai_recommendation && (
                            <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <Bot className="w-4 h-4 text-purple-600" />
                                <p className="text-sm font-medium text-purple-800">AI Recommendation</p>
                                {task.ai_confidence && (
                                  <span className="text-xs px-2 py-0.5 bg-purple-200 text-purple-700 rounded-full">
                                    {Math.round(task.ai_confidence * 100)}% confidence
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-purple-700 whitespace-pre-wrap">{task.ai_recommendation}</p>
                            </div>
                          )}
                          
                          {task.completion_notes && (
                            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                              <p className="text-sm font-medium text-green-800">Completion Notes</p>
                              <p className="text-sm text-green-700">{task.completion_notes}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* AI Result Modal */}
      {aiResultModal?.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Bot className="w-5 h-5 text-purple-600" />
                {aiResultModal.title}
              </h3>
              <button
                onClick={() => setAiResultModal(null)}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <AIResultDisplay 
                content={aiResultModal.content} 
                type={
                  aiResultModal.title.includes('Segmentación') ? 'segmentation' :
                  aiResultModal.title.includes('Comunicación') ? 'communication' :
                  aiResultModal.title.includes('Legal') ? 'legal_assessment' : 'general'
                }
              />
            </div>
            <div className="p-4 border-t flex justify-end">
              <button
                onClick={() => setAiResultModal(null)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Operator Assistant */}
      <OperatorAssistant 
        caseId={caseId} 
        debtorId={debtor?.debtor_id}
        context={{
          case_status: caseData?.status,
          debtor_name: debtor ? `${debtor.first_name} ${debtor.last_name || ''}` : undefined,
          total_amount: caseData?.total_amount,
          amount_paid: caseData?.amount_paid,
        }}
      />
    </div>
  );
}